import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CurrentUserResponse } from "@multi-chat/shared";
import { useAuth } from "./features/auth/auth-context.js";
import { useOrganizations } from "./features/organizations/organization-context.js";
import { LoginPage } from "./features/auth/LoginPage/LoginPage.js";
import { OrganizationSelectPage } from "./features/organizations/OrganizationSelectPage/OrganizationSelectPage.js";
import { hasEnteredWorkspace, isFreshAuthentication, markWorkspaceEntered } from "./features/organizations/workspace-entry.js";
import { shouldEnterWorkspaceDirectly } from "./features/organizations/organization-selection.js";
import { WhatsAppPage } from "./features/whatsapp/WhatsAppPage/WhatsAppPage.js";
import { ChannelsWorkspaceLayout } from "./features/channels/ChannelsWorkspaceLayout.js";
import { ChannelsHomePage } from "./features/channels/ChannelsHomePage.js";
import { FacebookChannelPage, InstagramChannelPage } from "./features/channels/PlatformChannelPages.js";
import { ContactsPage } from "./features/contacts/ContactsPage/ContactsPage.js";
import { CampaignsPage } from "./features/campaigns/CampaignsPage.js";
import { CampaignCreatePage } from "./features/campaigns/CampaignCreatePage.js";
import { CampaignDetailPage } from "./features/campaigns/CampaignDetailPage.js";
import {
  CampaignWorkspaceLayout,
  useCampaignWorkspace,
} from "./features/campaigns/CampaignWorkspaceLayout.js";
import { AllReportsPage } from "./features/reports/AllReportsPage.js";
import { OutgoingMessagesReportPage } from "./features/reports/OutgoingMessagesReportPage.js";
import { IncomingMessagesReportPage } from "./features/reports/IncomingMessagesReportPage.js";
import { MessageLogPage } from "./features/reports/MessageLogPage.js";
import { ContactImportReportsPage } from "./features/reports/ContactImportReportsPage.js";
import { CampaignReportPage } from "./features/reports/CampaignReportPage.js";
import { ReportsWorkspaceLayout, useReportsWorkspace } from "./features/reports/ReportsWorkspaceLayout.js";
import { SettingsWorkspaceLayout, useSettingsWorkspace } from "./features/settings/SettingsWorkspaceLayout.js";
import { GeneralSettingsPage } from "./features/settings/GeneralSettingsPage.js";
import { UsageSettingsPage } from "./features/settings/UsageSettingsPage.js";
import { TeamSettingsPage } from "./features/settings/TeamSettingsPage.js";
import { SecurityDataSettingsPage } from "./features/settings/SecurityDataSettingsPage.js";
import { useOrganizationBetaFeatures } from "./features/beta-features/use-organization-beta-features.js";
import { InboxPage } from "./features/inbox/InboxPage/InboxPage.js";
import { PostCommentsPage } from "./features/inbox/comments/PostCommentsPage.js";
import { InvitationAcceptPage } from "./features/team/InvitationAcceptPage.js";
import { ErrorState } from "./components/common/ErrorState/index.js";
import { AccessDeniedPage, NotFoundPage } from "./components/common/RouteStatus/index.js";
import { RouteStatusVisibilityProvider } from "./components/common/RouteStatus/route-status-visibility.js";
import { AppBootstrapLoader } from "./components/layout/AppBootstrapLoader.js";
import {
  appBootstrapEnterMotion,
  appBootstrapExitMotion,
  appWorkspaceRevealMotion,
} from "./components/layout/app-bootstrap-motion.js";
import {
  confirmsErrorRoute,
  isLoaderHoldActive,
  isWorkspaceEntryHold,
  loaderHoldCoversSignedOut,
} from "./components/layout/feedbot-loader-timing.js";
import { useFeedBotLoaderHold } from "./components/layout/use-feedbot-loader-hold.js";
import { WorkspaceSwitchLoaderProvider } from "./components/layout/workspace-switch-loader.js";
import { useDocumentTitle } from "./components/layout/use-document-title.js";
import { AuthenticatedShell } from "./components/layout/AuthenticatedShell.js";
import { SuperAdminApp } from "./features/super-admin/SuperAdminApp.js";
import { isSuperAdminPath } from "./features/super-admin/super-admin-path.js";
import { AnimatePresence, motion, useReducedMotion } from "./lib/motion.js";

export const LOGIN_PATH = "/login";
export const HOME_PATH = "/inbox";

/** Application paths that require a session. Unauthenticated visits are replaced with LOGIN_PATH. */
const PROTECTED_PATHS = [
  "/",
  "/inbox",
  "/post-comments",
  "/contacts",
  "/campaigns",
  "/reports",
  "/reports/campaigns",
  "/reports/outgoing",
  "/reports/incoming",
  "/reports/message-log",
  "/settings",
  "/settings/general",
  "/settings/usage",
  "/settings/plan-billing",
  "/settings/plan-usage",
  "/settings/team",
  "/settings/channels",
  "/settings/security-data",
  "/channels",
  "/channels/whatsapp",
  "/whatsapp",
  "/facebook",
  "/instagram",
  "/super-admin",
] as const;

export function App(): React.JSX.Element {
  const { t } = useTranslation();
  const { phase, logout, applyMemberDisplayName } = useAuth();
  const { organizations, selected, pendingOrganizationId } = useOrganizations();
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion() ?? false;
  const exitTransition = appBootstrapExitMotion(reduceMotion);
  const enterMotion = appBootstrapEnterMotion(reduceMotion);
  const loaderHold = useFeedBotLoaderHold({ onLoginPage: location.pathname === LOGIN_PATH });
  const startLoaderHold = loaderHold.start;
  const settleLoginPageRefresh = loaderHold.settleLoginPageRefresh;
  const releaseLoaderHold = loaderHold.release;
  useDocumentTitle();

  const handleLogout = useCallback(async () => {
    await logout();
    // Replace so the protected path does not stay in the address bar or in history.
    navigate(LOGIN_PATH, { replace: true });
  }, [logout, navigate]);

  // Session unresolved, or authenticated with memberships but no selected org yet.
  // Same loader throughout — never Login or protected content.
  const sessionUnresolved = phase.kind === "loading" || phase.kind === "authenticating";
  const orgUnresolved =
    phase.kind === "authenticated" &&
    !isSuperAdminPath(location.pathname) &&
    organizations.length > 0 &&
    selected === null;
  const booting = sessionUnresolved || orgUnresolved;

  // Minimum loader time for sign-in, workspace entry, and page loads. Readiness
  // is the existing gating above plus a pending organization resolving.
  const workspacePending =
    pendingOrganizationId !== null && selected?.id !== pendingOrganizationId;
  // 404/403 pages render only after the gating above resolves, so a mounted one
  // is a confirmed result: its minimum hold ends before the first covered paint.
  const [errorPageRegisteredAt, setErrorPageRegisteredAt] = useState<readonly number[]>([]);
  const registerErrorPage = useCallback(() => {
    const registeredAt = performance.now();
    setErrorPageRegisteredAt((current) => [...current, registeredAt]);
    return () =>
      setErrorPageRegisteredAt((current) => {
        const index = current.indexOf(registeredAt);
        return index === -1 ? current : current.toSpliced(index, 1);
      });
  }, []);
  const holdConditions = {
    destinationReady: !booting && !workspacePending,
    failed:
      phase.kind === "error" ||
      (phase.kind === "unauthenticated" && !loaderHoldCoversSignedOut(loaderHold.hold)),
    confirmedErrorRoute: confirmsErrorRoute(loaderHold.hold, errorPageRegisteredAt),
  };
  const holding = isLoaderHoldActive(loaderHold.hold, holdConditions);
  const releaseHold = loaderHold.hold !== null && !holding;
  const showLoader = booting || holding;
  // Read on the render the loader lifts, while the hold is still set; a later
  // transition change does not restart the running fade.
  const workspaceEntry = isWorkspaceEntryHold(loaderHold.hold);
  const coverWorkspace = showLoader && workspaceEntry;
  const surfaceTransition = workspaceEntry
    ? appWorkspaceRevealMotion({
        reduceMotion,
        immediate: holdConditions.failed || holdConditions.confirmedErrorRoute,
      })
    : enterMotion.transition;
  const routeStatusVisibility = useMemo(
    () => ({ revealed: !showLoader, register: registerErrorPage }),
    [showLoader, registerErrorPage],
  );
  const settleLoginRefresh =
    phase.kind === "authenticated" && loaderHold.hold?.kind === "loginPageRefresh";

  useEffect(() => {
    if (phase.kind === "authenticating") startLoaderHold("loginToSwitcher");
  }, [phase.kind, startLoaderHold]);

  useEffect(() => {
    if (settleLoginRefresh) settleLoginPageRefresh();
  }, [settleLoginRefresh, settleLoginPageRefresh]);

  useEffect(() => {
    if (releaseHold) releaseLoaderHold();
  }, [releaseHold, releaseLoaderHold]);

  const handleWorkspaceEntry = useCallback(() => {
    startLoaderHold("switcherToWorkspace");
  }, [startLoaderHold]);

  const beginWorkspaceSwitch = useCallback(() => {
    startLoaderHold("organizationSwitch");
  }, [startLoaderHold]);

  let resolved: React.JSX.Element | null = null;
  if (!booting) {
    if (phase.kind === "error") {
      resolved = (
        <ErrorState
          message={phase.message}
          onRetry={() => window.location.reload()}
          retryLabel={t("auth.retry")}
        />
      );
    } else if (phase.kind === "unauthenticated") {
      resolved = (
        <Routes>
          <Route path={LOGIN_PATH} element={<LoginPage />} />
          <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
          {PROTECTED_PATHS.map((path) => (
            <Route
              key={path}
              path={path}
              element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
            />
          ))}
          <Route
            path="campaigns/*"
            element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
          />
          <Route
            path="reports/*"
            element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
          />
          <Route
            path="settings/*"
            element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
          />
          <Route
            path="channels/*"
            element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
          />
          <Route
            path="super-admin/*"
            element={<Navigate to={{ pathname: LOGIN_PATH, search: location.search }} replace />}
          />
          <Route path="*" element={<NotFoundPage homePath={LOGIN_PATH} />} />
        </Routes>
      );
    } else if (phase.kind === "authenticated") {
      resolved = isSuperAdminPath(location.pathname) ? (
        <SuperAdminApp onLogout={() => void handleLogout()} />
      ) : (
        <AuthenticatedApp
          user={phase.user}
          onLogout={() => void handleLogout()}
          onDisplayNameSaved={applyMemberDisplayName}
          onWorkspaceEntry={handleWorkspaceEntry}
        />
      );
    }
  }

  return (
    <WorkspaceSwitchLoaderProvider value={beginWorkspaceSwitch}>
    <div className="relative h-dvh min-h-0">
      {booting ? null : (
        <motion.div
          key="resolved"
          className="h-full min-h-0"
          inert={showLoader}
          initial="initial"
          animate={coverWorkspace ? "covered" : "animate"}
          variants={enterMotion.variants}
          transition={surfaceTransition}
        >
          <RouteStatusVisibilityProvider value={routeStatusVisibility}>
            {resolved}
          </RouteStatusVisibilityProvider>
        </motion.div>
      )}
      <AnimatePresence>
        {showLoader ? (
          <motion.div
            key="bootstrap"
            className="fixed inset-0 z-50"
            initial={false}
            exit={{ opacity: 0, pointerEvents: "none", transition: exitTransition }}
          >
            <AppBootstrapLoader
              label={phase.kind === "authenticating" ? t("app.signingIn") : t("app.loading")}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
    </WorkspaceSwitchLoaderProvider>
  );
}

interface AuthenticatedAppProps {
  user: CurrentUserResponse;
  onLogout: () => void;
  onDisplayNameSaved: (organizationId: number, displayName: string) => void;
  /** The person entered a workspace from the organization switcher. */
  onWorkspaceEntry: () => void;
}

function AuthenticatedApp({
  user,
  onLogout,
  onDisplayNameSaved,
  onWorkspaceEntry,
}: AuthenticatedAppProps): React.JSX.Element {
  const location = useLocation();
  const { organizations, selected, pendingOrganizationId } = useOrganizations();
  const [entryVersion, setEntryVersion] = useState(0);
  const enterDirectly = shouldEnterWorkspaceDirectly({
    organizations,
    pendingInvitationCount: user.pendingInvitations.length,
    storedId: selected?.id ?? null,
    freshAuthentication: isFreshAuthentication(),
    workspaceEntered: hasEnteredWorkspace(),
    platformAdmin: user.platformAdmin,
  });
  void entryVersion;

  if (location.pathname === "/invitations/accept") {
    return <InvitationAcceptPage />;
  }

  if (pendingOrganizationId !== null && selected?.id !== pendingOrganizationId) {
    return <AppBootstrapLoader />;
  }

  if (!enterDirectly) {
    return (
      <OrganizationSelectPage
        onContinue={() => {
          markWorkspaceEntered();
          onWorkspaceEntry();
          setEntryVersion((version) => version + 1);
        }}
      />
    );
  }

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  return (
    <Routes>
      <Route
        element={
          <AuthenticatedShell
            user={user}
            onLogout={onLogout}
            onDisplayNameSaved={onDisplayNameSaved}
          />
        }
      >
        <Route index element={<Navigate to={HOME_PATH} replace />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="post-comments" element={<PostCommentsRoute />} />
        <Route path="contacts" element={<ContactsRoute />} />
        <Route path="campaigns" element={<CampaignWorkspaceRoute />}>
          <Route element={<CampaignsPage />}>
            <Route index element={null} />
            <Route path="new" element={<CampaignCreateRoute />} />
          </Route>
          <Route path=":campaignId" element={<CampaignDetailRoute />} />
        </Route>
        <Route path="reports" element={<ReportsWorkspaceRoute />}>
          <Route index element={<AllReportsPage />} />
          <Route path="campaigns" element={<CampaignReportsRoute />} />
          <Route path="outgoing" element={<OutgoingReportsRoute />} />
          <Route path="incoming" element={<IncomingReportsRoute />} />
          <Route path="message-log" element={<MessageLogRoute />} />
          <Route path="contact-imports" element={<ContactImportReportsRoute />} />
        </Route>
        <Route path="settings" element={<SettingsWorkspaceRoute />}>
          <Route index element={<Navigate to="/settings/general" replace />} />
          <Route path="general" element={<GeneralSettingsPage />} />
          <Route path="usage" element={<OwnerAdminSettingsRoute />} />
          <Route path="plan-billing" element={<Navigate to="/settings/general" replace />} />
          <Route path="plan-usage" element={<Navigate to="/settings/usage" replace />} />
          <Route path="team" element={<TeamSettingsPage />} />
          <Route path="channels" element={<Navigate to="/channels" replace />} />
          <Route path="security-data" element={<SecurityDataSettingsPage />} />
        </Route>
        <Route path="channels" element={<ChannelsWorkspaceRoute />}>
          <Route index element={<ChannelsHomePage />} />
          <Route path="whatsapp" element={<WhatsAppRoute />} />
          <Route path="facebook" element={<FacebookChannelPage />} />
          <Route path="instagram" element={<InstagramChannelPage />} />
        </Route>
        <Route path="whatsapp" element={<LegacyWhatsAppRedirect />} />
        <Route path="facebook" element={<Navigate to="/channels" replace />} />
        <Route path="instagram" element={<Navigate to="/channels" replace />} />
      </Route>
      <Route path="login" element={<Navigate to={HOME_PATH} replace />} />
      <Route path="*" element={<NotFoundPage homePath={HOME_PATH} />} />
    </Routes>
  );
}

function ChannelsWorkspaceRoute(): React.JSX.Element {
  const { selected } = useOrganizations();
  if (!selected) return <AppBootstrapLoader />;
  return <ChannelsWorkspaceLayout organizationId={selected.id} role={selected.role} />;
}

function LegacyWhatsAppRedirect(): React.JSX.Element {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/channels/whatsapp", search: location.search, hash: location.hash }}
      replace
      state={location.state}
    />
  );
}

function WhatsAppRoute(): React.JSX.Element {
  const { selected } = useOrganizations();

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  return <WhatsAppPage organizationId={selected.id} role={selected.role} />;
}

function OwnerAdminSettingsRoute(): React.JSX.Element {
  const { role } = useSettingsWorkspace();

  if (role !== "OWNER" && role !== "ADMIN") {
    return <AccessDeniedPage />;
  }

  return <UsageSettingsPage />;
}

function PostCommentsRoute(): React.JSX.Element {
  const { selected } = useOrganizations();
  const { loading, hasBetaFeature } = useOrganizationBetaFeatures();

  if (!selected) {
    return <AppBootstrapLoader />;
  }
  if (loading) {
    return <AppBootstrapLoader />;
  }
  if (!hasBetaFeature("POST_COMMENTS")) {
    return <Navigate to={HOME_PATH} replace />;
  }

  return <PostCommentsPage />;
}

function ContactsRoute(): React.JSX.Element {
  const { selected } = useOrganizations();

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  return <ContactsPage organizationId={selected.id} />;
}

function ReportsWorkspaceRoute(): React.JSX.Element {
  const { selected } = useOrganizations();

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  if (selected.role !== "OWNER" && selected.role !== "ADMIN") {
    return <AccessDeniedPage />;
  }

  return (
    <ReportsWorkspaceLayout organizationId={selected.id} role={selected.role} />
  );
}

function OutgoingReportsRoute(): React.JSX.Element {
  const { organizationId, role } = useReportsWorkspace();
  return <OutgoingMessagesReportPage organizationId={organizationId} role={role} />;
}

function CampaignReportsRoute(): React.JSX.Element {
  const { organizationId, role } = useReportsWorkspace();
  return <CampaignReportPage organizationId={organizationId} role={role} />;
}

function IncomingReportsRoute(): React.JSX.Element {
  const { organizationId, role } = useReportsWorkspace();
  return <IncomingMessagesReportPage organizationId={organizationId} role={role} />;
}

function MessageLogRoute(): React.JSX.Element {
  const { organizationId, role } = useReportsWorkspace();
  return <MessageLogPage organizationId={organizationId} role={role} />;
}

function ContactImportReportsRoute(): React.JSX.Element {
  const { organizationId, role } = useReportsWorkspace();
  return <ContactImportReportsPage organizationId={organizationId} role={role} />;
}

function SettingsWorkspaceRoute(): React.JSX.Element {
  const { selected } = useOrganizations();

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  return (
    <SettingsWorkspaceLayout organizationId={selected.id} role={selected.role} />
  );
}

function CampaignWorkspaceRoute(): React.JSX.Element {
  const { selected } = useOrganizations();
  const detailMatch = useMatch("/campaigns/:campaignId");
  const parsedCampaignId = Number(detailMatch?.params.campaignId);
  const selectedCampaignId =
    Number.isInteger(parsedCampaignId) && parsedCampaignId > 0 ? parsedCampaignId : null;

  if (!selected) {
    return <AppBootstrapLoader />;
  }

  return (
    <CampaignWorkspaceLayout
      organizationId={selected.id}
      role={selected.role}
      canCreate={selected.role === "OWNER" || selected.role === "ADMIN"}
      selectedCampaignId={selectedCampaignId}
    />
  );
}

function CampaignCreateRoute(): React.JSX.Element {
  const { organizationId, role, canCreate } = useCampaignWorkspace();

  if (!canCreate && (role === "OWNER" || role === "ADMIN")) {
    return <Navigate to="/campaigns" replace />;
  }

  if (role !== "OWNER" && role !== "ADMIN") {
    return (
      <div className="fixed inset-0 z-[var(--z-alert)] overflow-auto bg-canvas">
        <AccessDeniedPage />
      </div>
    );
  }

  return <CampaignCreatePage organizationId={organizationId} />;
}

function CampaignDetailRoute(): React.JSX.Element {
  const { organizationId, role, selectedCampaignId } = useCampaignWorkspace();

  if (selectedCampaignId === null) {
    return <NotFoundPage homePath={HOME_PATH} />;
  }

  return (
    <CampaignDetailPage
      organizationId={organizationId}
      campaignId={selectedCampaignId}
      role={role}
    />
  );
}
